import { Box, Flex, Stack, desktopFirstMediaQuery } from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import useCheckMyProfile from '../../../../../../hooks/useCheckMyProfile';
import useInstructorProfileQuery from '../../../../../../hooks/useInstructorProfileQuery';
import useQueryParams from '../../../../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../../../../hooks/useUserProfileQuery';
import useClientAccountProfileQuery from '../../../../../../queries/useClientAccountProfileQuery';
import { formatCommaNumber } from '../../../../../../utils/format/number';
import { AvatarImage } from '../AvatarImage';
import { FollowingButton } from '../FollowingButton';
import { HubProfile } from '../HubProfile';
import { ProfileEditButton } from '../ProfileEditButton';
import { RatingItem } from '../RatingItem';
import { StatItem } from '../StatItem';
import { UserName } from '../UserName';
import { UserSlug } from '../UserSlug';
import { UserSlugModal } from '../UserSlugModal';
import { MentoringApplyButton } from './MentoringApplyButton';
import { MentoringEditButton } from './MentoringEditButton';
import { MentoringInformation } from './MentoringInformation';

const LazyCertificationAddSlot = dynamic(() => import('./CertificationAddSlot'), { ssr: false });
const LazyInflearnGuaranteedBadge = dynamic(() => import('././InflearnGuaranteedBadge'), {
  ssr: false,
});

export function InstructorProfile() {
  const [isSlugModalOpened, slugModalHandler] = useDisclosure(false);
  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();
  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const { data: myProfile } = useClientAccountProfileQuery({ enabled: isMe });
  const { data: instructorProfile } = useInstructorProfileQuery({
    userId: String(id),
    isInstructor: true,
  });
  const { t } = useTranslation();
  const { isKo } = useLang();

  useEffect(() => {
    if (myProfile && !myProfile.isSlugConfirmed) {
      slugModalHandler.open();
    }
  }, [myProfile]);

  if (!profile || !instructorProfile) {
    return null;
  }

  const isActiveMentor = isKo && Boolean(instructorProfile.mentor?.isActive);

  return (
    <>
      <Stack gap={24}>
        <Flex
          justify="space-between"
          styles={{
            root: {
              [desktopFirstMediaQuery.mobile]: {
                flexDirection: 'column',
                gap: 24,
              },
            },
          }}
        >
          <Flex
            align="center"
            styles={{
              root: {
                gap: 24,
                [desktopFirstMediaQuery.mobile]: {
                  gap: 20,
                },
              },
            }}
          >
            <Box css={{ flexShrink: 0 }}>
              <AvatarImage src={profile.thumbnailUrl} />
            </Box>
            <Stack gap={4}>
              <Flex align="center" wrap="wrap" rowGap={4} columnGap={10}>
                <UserName name={profile.name} />
                <ErrorBoundary fallback={null}>
                  <Flex
                    align="center"
                    wrap="wrap"
                    rowGap={4}
                    columnGap={10}
                    styles={{
                      root: {
                        // 표시할 뱃지가 없으면 빈 컨테이너로 인한 여백/gap이 생기지 않도록 숨김
                        '&:empty': {
                          display: 'none',
                        },
                        // 모바일 뷰에서는 뱃지를 항상 이름 아랫줄에 함께 표시
                        [desktopFirstMediaQuery.mobile]: {
                          flexBasis: '100%',
                        },
                      },
                    }}
                  >
                    <LazyInflearnGuaranteedBadge
                      isInflearnGuaranteed={profile.isInflearnGuaranteed}
                    />
                    <LazyCertificationAddSlot
                      isMe={isMe}
                      isCareerVerified={profile.isCareerVerified}
                    />
                  </Flex>
                </ErrorBoundary>
              </Flex>
              <UserSlug slug={profile.slug} />

              <MentoringInformation />
              <HubProfile userId={profile.id} userName={profile.name} />
            </Stack>
          </Flex>
          <Flex
            gap={10}
            styles={{
              root: {
                [desktopFirstMediaQuery.mobile]: {
                  width: '100%',
                  '& > button, & > a': {
                    flex: 1,
                  },
                },
              },
            }}
          >
            {isMe && <ProfileEditButton />}
            {isMe && isActiveMentor && <MentoringEditButton />}
            {!isMe && <FollowingButton />}
            {!isMe && isActiveMentor && <MentoringApplyButton />}
          </Flex>
        </Flex>
        <Box
          css={{
            display: 'grid',
            width: '100%',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 12,
          }}
        >
          <StatItem
            label={t('수강생')}
            content={
              instructorProfile.studentCount === 0
                ? '-'
                : formatCommaNumber(instructorProfile.studentCount)
            }
          />
          <StatItem
            label={t('수강평')}
            content={
              instructorProfile.reviewCount === 0
                ? '-'
                : formatCommaNumber(instructorProfile.reviewCount)
            }
          />
          <StatItem
            label={t('강의 평점')}
            content={
              instructorProfile.reviewAverage === '0.0' ? (
                '-'
              ) : (
                <RatingItem content={instructorProfile.reviewAverage} />
              )
            }
          />

          {isActiveMentor && (
            <>
              <StatItem
                label={t('함께한 멘티')}
                content={
                  !instructorProfile.mentor?.mentoringCount ||
                  instructorProfile.mentor.mentoringCount === 0
                    ? '-'
                    : formatCommaNumber(instructorProfile.mentor.mentoringCount)
                }
              />
              <StatItem
                label={t('멘토링 리뷰')}
                content={
                  !instructorProfile.mentor?.reviewCount ||
                  instructorProfile.mentor.reviewCount === 0
                    ? '-'
                    : formatCommaNumber(instructorProfile.mentor.reviewCount)
                }
              />
              <StatItem
                label={t('멘토링 평점')}
                content={
                  !instructorProfile.mentor?.reviewAverage ||
                  instructorProfile.mentor.reviewAverage === '0.0' ? (
                    '-'
                  ) : (
                    <RatingItem content={instructorProfile.mentor.reviewAverage} />
                  )
                }
              />
            </>
          )}
        </Box>
      </Stack>
      {myProfile && (
        <UserSlugModal
          userId={myProfile.id}
          nickname={myProfile.name}
          thumbnailUrl={myProfile.thumbnail}
          initialSlug={myProfile.slug || ''}
          isOpened={isSlugModalOpened}
          onClose={slugModalHandler.close}
        />
      )}
    </>
  );
}
